import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import Consultation from "@/components/sections/Consultation";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import { Link } from "@/lib/router-compat";
import {
  RelatedPages,
  allEducationLinks,
  allTreatmentsLinks,
  allServiceLinks,
} from "@/components/RelatedPages";
import {
  Clock,
  Calendar,
  CheckCircle2,
  ShieldCheck,
  Brain,
  Activity,
  Headphones,
  BarChart3,
  HelpCircle,
  Zap,
} from "lucide-react";
const TypicalTmsTreatment = () => {
  const courseHighlights = [
    {
      text: "Average of 30–36 treatment sessions",
      icon: Clock,
    },
    {
      text: "Conducted over a 4–6 week acute treatment period",
      icon: Calendar,
    },
    {
      text: "5 treatments per week (Monday through Friday)",
      icon: Activity,
    },
    {
      text: "Each session lasts approximately 19–37 minutes",
      icon: Zap,
    },
  ];
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={
          "Typical TMS Treatment Course | What To Expect | Neuro Wellness TMS Centers of America"
        }
        description={
          "What to expect during a typical TMS treatment course. Learn about session duration (20-40 mins), motor threshold mapping, 4-6 week frequency, and daily recovery in Coral Springs, FL."
        }
        canonical={"/contents/tms/typical-tms-treatment"}
        keywords={
          "Typical TMS treatment course, what to expect TMS session, motor threshold calibration, DLPFC stimulation, TMS session length, daily depression treatment Coral Springs"
        }
        schemaJson={[
          {
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            name: "Typical TMS Treatment Course",
            description:
              "Step-by-step breakdown of what to expect during an acute TMS treatment protocol for depression.",
            url: "/contents/tms/typical-tms-treatment/",
          },
        ]}
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        {/* Hero Banner */}
        <section className="relative overflow-hidden bg-gradient-navy text-primary-foreground py-16 sm:py-24">
          <div className="pointer-events-none absolute -right-20 top-0 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />
          <div className="container-px mx-auto max-w-7xl">
            <div className="max-w-3xl space-y-4 animate-fade-up">
              <span className="inline-block rounded-full bg-accent/20 px-4 py-1.5 text-xs sm:text-sm font-bold text-accent uppercase tracking-wider">
                TMS Education • Treatment Protocol
              </span>
              <h1 className="font-headings text-3xl font-extrabold text-white sm:text-5xl md:text-6xl">
                Typical TMS Treatment
              </h1>
              <p className="text-lg sm:text-2xl text-accent font-semibold">
                What To Expect Before, During, and After Your TMS Sessions
              </p>
              <p className="text-base sm:text-lg text-primary-foreground/80 leading-relaxed max-w-2xl">
                Understanding the routine of a standard TMS treatment course
                removes fear of the unknown. Explore session timelines, motor
                threshold mapping, and comfort measures.
              </p>
              <div className="pt-4 flex flex-wrap gap-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
                >
                  <a href="#consultation">Schedule Consultation</a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-2 border-white/50 text-white bg-transparent hover:bg-white/10"
                >
                  <a href="tel:9548279793">Call 954-827-9793</a>
                </Button>
              </div>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 overflow-hidden leading-none pointer-events-none">
            <svg
              className="relative block w-full h-8 sm:h-12 text-background"
              viewBox="0 0 1200 120"
              preserveAspectRatio="none"
              fill="currentColor"
            >
              <path d="M0,0 C150,90 350,-40 500,50 C650,140 900,10 1200,40 L1200,120 L0,120 Z"></path>
            </svg>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-12 sm:py-20 bg-background">
          <div className="container-px mx-auto max-w-5xl space-y-12">
            {/* Quick Course Schedule Box */}
            <div className="rounded-2xl bg-card border border-border p-6 sm:p-10 shadow-soft space-y-6">
              <h2 className="font-headings text-2xl sm:text-3xl font-extrabold text-primary">
                Treatment Course at a Glance
              </h2>
              <p className="text-base sm:text-lg text-foreground/90 leading-relaxed font-normal">
                Although exact protocols are customized to each individual, a
                standard FDA-cleared TMS treatment course for Major Depressive
                Disorder follows this schedule:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                {courseHighlights.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={idx}
                      className="flex items-center gap-3.5 bg-light-blue/30 p-4 rounded-xl border border-light-blue"
                    >
                      <div className="h-10 w-10 rounded-lg bg-primary text-white flex items-center justify-center shrink-0 font-bold">
                        <Icon className="h-5 w-5" />
                      </div>
                      <span className="font-bold text-primary text-sm sm:text-base">
                        {item.text}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* What Happens During Treatment */}
            <div className="rounded-2xl bg-card border border-border p-6 sm:p-10 shadow-soft space-y-6">
              <h2 className="font-headings text-2xl sm:text-3xl font-extrabold text-primary">
                What to Expect During a TMS Session
              </h2>
              <div className="space-y-4 text-base sm:text-lg text-foreground/90 leading-relaxed">
                <p>
                  During TMS treatment, you relax in a comfortable recline chair
                  while remaining fully awake and alert. The TMS Therapy device
                  delivers targeted magnetic pulses to specific areas of your
                  brain known to regulate mood.
                </p>
                <p>
                  These pulses produce a tapping sensation on the scalp. Your
                  TMS technician can make instant adjustments if the tapping
                  feels uncomfortable. Mild scalp discomfort or a slight
                  headache may sometimes occur during or immediately after early
                  sessions; over-the-counter pain relievers easily manage this.
                  If discomfort persists, the magnetic intensity can be
                  temporarily lowered to ensure complete comfort.
                </p>
                <p>
                  After each treatment session, patients can immediately return
                  to their normal daily routine—including driving back to work
                  or home. In clinical trials, most patients experienced a
                  positive response by the fourth week of treatment, though some
                  notice improvements earlier.
                </p>
              </div>

              {/* Study Response Callout */}
              <div className="p-5 rounded-xl bg-accent/10 border border-accent/30 space-y-2">
                <span className="text-xs font-bold uppercase tracking-wider text-accent">
                  Clinical Response Outcome
                </span>
                <p className="font-bold text-primary text-base sm:text-lg">
                  In a trial of 82 patients evaluated with the IDS-SR30 severity
                  scale, 67.1% positively responded to TMS and 46.3% achieved
                  full remission with zero significant adverse events reported.
                </p>
              </div>
            </div>

            {/* First Session & Motor Threshold Calibration */}
            <div className="rounded-2xl bg-light-blue/30 border border-light-blue p-6 sm:p-10 shadow-soft space-y-6">
              <div className="flex items-center gap-3 text-primary">
                <div className="h-10 w-10 rounded-xl bg-accent text-white flex items-center justify-center font-bold">
                  <Brain className="h-5 w-5" />
                </div>
                <h2 className="font-headings text-2xl sm:text-3xl font-extrabold">
                  Your First Session & Motor Threshold Test
                </h2>
              </div>

              <div className="space-y-4 text-base sm:text-lg text-foreground/85 leading-relaxed font-normal">
                <p>
                  Because it is vital to personalize treatment parameters, your
                  initial session takes up to 90 minutes. A licensed
                  psychiatrist or trained practitioner determines the exact
                  placement and power settings of the treatment coil.
                </p>
                <p>
                  You will be provided with earplugs to soften the audible
                  clicking sound emitted by the magnetic coil. You are welcome
                  to bring your own non-metallic headphones to listen to music
                  or podcasts during sessions.
                </p>
                <p>
                  To calibrate your dosage, a test is performed to measure your{" "}
                  <strong className="text-primary font-bold">
                    Motor Threshold
                  </strong>
                  —the minimum magnetic field strength that produces a subtle
                  movement in the fingers of your right hand. Once calculated,
                  the treatment coil is positioned over the Dorsolateral
                  Prefrontal Cortex (DLPFC) in the left frontal lobe, where
                  therapeutic stimulation begins.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="flex items-start gap-3 bg-card p-4 rounded-xl border border-border shadow-xs">
                  <Headphones className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-primary text-sm">
                      Comfort & Entertainment
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Earplugs provided; listen to music or talk with your
                      technician.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-card p-4 rounded-xl border border-border shadow-xs">
                  <ShieldCheck className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-primary text-sm">
                      No Recovery Time
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Drive home or back to office with no downtime or
                      restriction.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sub-navigation Directory */}
            <div className="rounded-2xl bg-gradient-navy text-primary-foreground p-6 sm:p-10 space-y-6">
              <h3 className="font-headings text-xl sm:text-2xl font-bold text-white text-center">
                Explore Additional TMS Pages
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Link
                  to="/contents/tms/what-is-tms-therapy"
                  className="bg-white/10 hover:bg-white/20 p-4 rounded-xl border border-white/20 transition-all text-center space-y-2 group"
                >
                  <Brain className="h-6 w-6 text-accent mx-auto" />
                  <p className="font-bold text-white text-sm group-hover:text-accent transition-colors">
                    What Is TMS Therapy?
                  </p>
                  <p className="text-xs text-primary-foreground/75">
                    Mechanism of action & benefits
                  </p>
                </Link>

                <Link
                  to="/contents/tms/is-tms-right-for-me"
                  className="bg-white/10 hover:bg-white/20 p-4 rounded-xl border border-white/20 transition-all text-center space-y-2 group"
                >
                  <HelpCircle className="h-6 w-6 text-accent mx-auto" />
                  <p className="font-bold text-white text-sm group-hover:text-accent transition-colors">
                    Is TMS Right For Me?
                  </p>
                  <p className="text-xs text-primary-foreground/75">
                    Candidacy criteria & self-quiz
                  </p>
                </Link>

                <Link
                  to="/contents/tms/tms-clinical-study-results"
                  className="bg-white/10 hover:bg-white/20 p-4 rounded-xl border border-white/20 transition-all text-center space-y-2 group"
                >
                  <BarChart3 className="h-6 w-6 text-accent mx-auto" />
                  <p className="font-bold text-white text-sm group-hover:text-accent transition-colors">
                    Clinical Study Results
                  </p>
                  <p className="text-xs text-primary-foreground/75">
                    Dunner, Vaché & Cress research data
                  </p>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Consultation Component */}
        <RelatedPages
          heading="Explore Related TMS Resources"
          links={[
            ...allEducationLinks.filter(
              (l) => l.href !== "/contents/tms/typical-tms-treatment/",
            ),
            ...allTreatmentsLinks,
            ...allServiceLinks,
          ]}
        />
        <Consultation />
      </main>

      <Footer />
    </div>
  );
};
export default TypicalTmsTreatment;
